import React, { useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { alpha } from "@mui/material/styles";
import EloSparklineChart, { padSparkChartPoints } from "../common/EloSparklineChart";
import { colors } from "../../theme/colors";

const SPARKLINE_MATCH_LIMIT = 7;
const BAR_SLOT_COUNT = 7;

function normalizeMatchTimestamp(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n < 1e12 ? n * 1000 : n;
}

function getMatchesSortedChrono(last25MatchData) {
    return Object.values(last25MatchData || {})
        .filter(Boolean)
        .sort(
            (a, b) =>
                (normalizeMatchTimestamp(a.timestamp) || 0) -
                (normalizeMatchTimestamp(b.timestamp) || 0)
        );
}

function buildMatchesBarData(matchesChrono) {
    const dayCounts = new Map();

    for (const match of matchesChrono) {
        const ts = normalizeMatchTimestamp(match.timestamp);
        if (ts == null) continue;

        const dayStart = new Date(ts);
        dayStart.setHours(0, 0, 0, 0);
        const dayKey = dayStart.getTime();
        dayCounts.set(dayKey, (dayCounts.get(dayKey) || 0) + 1);
    }

    if (dayCounts.size > 0) {
        const recentDays = [...dayCounts.entries()]
            .sort((a, b) => a[0] - b[0])
            .slice(-BAR_SLOT_COUNT)
            .map(([_, count], idx) => ({ idx, count }));

        while (recentDays.length < BAR_SLOT_COUNT) {
            recentDays.unshift({ idx: 0, count: 0 });
        }

        return recentDays
            .slice(-BAR_SLOT_COUNT)
            .map((entry, idx) => ({ idx, count: entry.count }));
    }

    const recentMatches = matchesChrono.slice(-BAR_SLOT_COUNT);
    if (recentMatches.length === 0) {
        return Array.from({ length: BAR_SLOT_COUNT }, (_, idx) => ({ idx, count: 0 }));
    }

    const bars = recentMatches.map((_, idx) => ({ idx, count: 1 }));
    while (bars.length < BAR_SLOT_COUNT) {
        bars.unshift({ idx: 0, count: 0 });
    }

    return bars.slice(-BAR_SLOT_COUNT).map((entry, idx) => ({ idx, count: entry.count }));
}

export function buildProfileStatsBarCharts(last25MatchData) {
    const matchesChrono = getMatchesSortedChrono(last25MatchData);
    const recentMatches = matchesChrono.slice(-SPARKLINE_MATCH_LIMIT);

    const pickMetric = (match, metric) => {
        if (metric === "adr") {
            const n = Number(match.adr);
            return Number.isFinite(n) ? n : null;
        }
        if (metric === "kd") {
            const kills = Number(match.kills);
            const deaths = Number(match.deaths);
            if (!Number.isFinite(kills)) return null;
            return kills / Math.max(Number.isFinite(deaths) ? deaths : 0, 1);
        }
        if (metric === "rating3") {
            const n = Number(match.rating3);
            return Number.isFinite(n) ? n : null;
        }
        return null;
    };

    const buildSparklineValues = (metric) =>
        recentMatches
            .map((match) => pickMetric(match, metric))
            .filter((value) => value != null);

    return {
        matchesByDay: buildMatchesBarData(matchesChrono),
        adrValues: buildSparklineValues("adr"),
        kdValues: buildSparklineValues("kd"),
        rating3Values: buildSparklineValues("rating3"),
    };
}

const CHART_BOX_SX = {
    width: "4.5rem",
    height: "2.25rem",
    flexShrink: 0,
    pointerEvents: "none",
};

const RING_BOX_SX = {
    width: "3.5rem",
    height: "3.5rem",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
};

export function StatBarChart({ data = [] }) {
    const bars =
        data.length > 0
            ? data
            : Array.from({ length: BAR_SLOT_COUNT }, (_, idx) => ({ idx, count: 0 }));
    const maxCount = Math.max(1, ...bars.map((bar) => Number(bar.count) || 0));

    return (
        <Box
            sx={{
                ...CHART_BOX_SX,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: "3px",
            }}
        >
            {bars.map((bar, index) => {
                const count = Number(bar.count) || 0;
                const heightPct = count > 0 ? Math.max((count / maxCount) * 100, 18) : 8;

                return (
                    <Box
                        key={`match-bar-${index}`}
                        sx={{
                            flex: 1,
                            height: `${heightPct}%`,
                            minHeight: count > 0 ? "0.35rem" : "0.15rem",
                            borderRadius: "2px 2px 0 0",
                            bgcolor: colors.primary.main,
                            opacity: count > 0 ? 1 : 0.18,
                        }}
                    />
                );
            })}
        </Box>
    );
}

export function StatRingChart({ value = 0 }) {
    const clamped = Math.max(0, Math.min(100, Number(value) || 0));

    return (
        <Box sx={RING_BOX_SX}>
            <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress
                    variant="determinate"
                    value={100}
                    size={56}
                    thickness={4}
                    sx={{ color: alpha(colors.text.primary, 0.08) }}
                />
                <CircularProgress
                    variant="determinate"
                    value={clamped}
                    size={56}
                    thickness={4}
                    sx={{
                        color: colors.primary.main,
                        position: "absolute",
                        left: 0,
                        "& .MuiCircularProgress-circle": {
                            strokeLinecap: "round",
                        },
                    }}
                />
            </Box>
        </Box>
    );
}

export function StatMetricSparkline({ values = [] }) {
    const chartPoints = useMemo(() => {
        const numeric = (values || []).filter((value) => Number.isFinite(Number(value)));
        if (numeric.length === 0) return [];

        const points = numeric.map((value) => ({
            elo: Number(value),
            change: 0,
            result: "—",
        }));

        return padSparkChartPoints(points);
    }, [values]);

    if (chartPoints.length < 2) {
        return <Box sx={CHART_BOX_SX} />;
    }

    return (
        <Box sx={CHART_BOX_SX}>
            <EloSparklineChart
                points={chartPoints}
                monochrome
                curveType="monotone"
                chartMargin={{ top: 6, right: 2, left: 2, bottom: 4 }}
                yPadRatio={0.18}
                segmentStrokeWidth={1.75}
            />
        </Box>
    );
}
